import { breakpoints } from 'hfs-common-ui';
import { css, CSSObject, DefaultTheme, Interpolation } from 'styled-components';
import { OperationalStatusEnum } from 'fleet-data-model';
import type { RasterColor } from '~/domain/greenkeeper-tool/models/RasterParams';
import opacityMap from './data/hexOpacity.json';

type Breakpoints = keyof typeof breakpoints;

export const colors = {
  cautionLight64: '#FFEBA3',
  errorLight64: '#F2B2B5',
  progressLight16: '#2FBCE2',
  progressLight32: '#56C9E7',
  progressLight64: '#A6E2F2',
  successLight64: '#B5E6A3',
};

export const paddings = {
  hugePadding: '32px',
  largePadding: '20px',
  defaultPadding: '16px',
  mediumPadding: '12px',
  smallPadding: '8px',
  tinyPadding: '4px',
  medium2xPadding: '24px',
};

/** Font sizes from .pcss */
/**
 *  This is legacy values imported from older `.pcss` file. Use new fontSizes object instead for new designs.
 * @param mainFontSize
 * @returns Object with font sizes
 */

const createFontSizes = (mainFontSize) => {
  return {
    mainFontSize: `${mainFontSize}px`,
    fontSizeHeader1: `calc(${mainFontSize}px * 3)`, // 48px
    fontSizeHeader2: `calc(${mainFontSize}px * 2)`, // 32px
    fontSizeHeader3: `calc(${mainFontSize}px * 1.5)`, // 24px
    fontSizeHeader4: `calc(${mainFontSize}px * 1.125)`, // 18px
    fontSizePrimary: `calc(${mainFontSize}px * 1.125)`, // 18px
    fontSizeBody: `${mainFontSize}px`, // 16px
    fontSizeSmallTitle: `calc(${mainFontSize}px * 0.875)`, // 14px
    fontSizeCaption: `calc(${mainFontSize}px * 0.75)`, // 12px
    fontSizeButton: `calc(${mainFontSize}px * 1.125)`, // 18px
    fontSizeIcon: `calc(${mainFontSize}px * 4)`, // 64px
  };
};

// Helper to convert RGB array [0-1] to hex color
export const rgbToHex = (rgb: RasterColor): string => {
  const r = Math.round(rgb[0] * 255);
  const g = Math.round(rgb[1] * 255);
  const b = Math.round(rgb[2] * 255);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b
    .toString(16)
    .padStart(2, '0')}`;
};

// Helper to convert hex color to RGB array [0-1]
export const hexToRgb = (hex: string): RasterColor => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    return [0, 0, 0];
  }
  return [
    Number.parseInt(result[1], 16) / 255,
    Number.parseInt(result[2], 16) / 255,
    Number.parseInt(result[3], 16) / 255,
  ];
};

/**
 *  This is legacy values imported from older `.pcss` file. Use new fontSizes object instead for new designs.
 */
export const fontSizes = createFontSizes(16);

export const spacing = {
  standard: '1rem',
};

export const shadows = {
  boxShadow2: '0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23)',
  elevatedPanel: '0 4px 10px rgba(0,0,0,0.25), 0 -4px  10px rgba(0,0,0,0.12)',
};

export const borderRadius = {
  default: '16px',
  secondary: '12px',
  small: '8px',
};

export const headerHeightValue = 77;
export const headerHeight = `${headerHeightValue}px`;
export const contentOverLayMinHeight = 140;
export const contentOverlayMinTopPosition = 148;
export const modalTitleHeight = '55px';
export const modalFooterHeight = '80px';
export const menubarHeight = '60px';

/**
 * Utility to generate media query strings for min-width breakpoints.
 */
const up = Object.keys(breakpoints).reduce((acc, key) => {
  acc[key as Breakpoints] = (literals: TemplateStringsArray, ...placeholders: any[]) => css`
    @media (min-width: ${breakpoints[key as Breakpoints]}px) {
      ${css(literals, ...placeholders)};
    }
  `;
  return acc;
}, {} as { [key in Breakpoints]: (literals: TemplateStringsArray, ...placeholders: any[]) => Interpolation<any> });

/**
 * Utility to generate media query strings for max-width breakpoints.
 */
const down = Object.keys(breakpoints).reduce((acc, key) => {
  acc[key as Breakpoints] = (literals: TemplateStringsArray, ...placeholders: any[]) => css`
    @media (max-width: ${breakpoints[key as Breakpoints]}px) {
      ${css(literals, ...placeholders)};
    }
  `;
  return acc;
}, {} as { [key in Breakpoints]: (literals: TemplateStringsArray, ...placeholders: any[]) => Interpolation<any> });

/**
 * Utility to generate media query strings for a range between two breakpoints.
 * @param start - The start breakpoint.
 * @param end - The end breakpoint.
 */
const between =
  (start: Breakpoints, end: Breakpoints) =>
  (literals: TemplateStringsArray, ...placeholders: any[]) =>
    css`
      @media (min-width: ${breakpoints[start]}px) and (max-width: ${breakpoints[end]}px) {
        ${css(literals, ...placeholders)};
      }
    `;

/**
 * Device media queries for breakpoints.
 */
export const deviceMedia = {
  down: Object.keys(breakpoints).reduce((acc, key) => {
    acc[key as Breakpoints] = `(max-width: ${breakpoints[key as Breakpoints]}px)`;
    return acc;
  }, {} as { [key in Breakpoints]: string }),
  up: Object.keys(breakpoints).reduce((acc, key) => {
    acc[key as Breakpoints] = `(min-width: ${breakpoints[key as Breakpoints]}px)`;
    return acc;
  }, {} as { [key in Breakpoints]: string }),
  between: (start: Breakpoints, end: Breakpoints) =>
    `(min-width: ${breakpoints[start]}px) and (max-width: ${breakpoints[end]}px)`,
};

/**
 * Generates a container query CSS block for a specified range of container widths.
 *
 * @param minWidth - The minimum width of the container in pixels.
 * @param maxWidth - The maximum width of the container in pixels.
 * @param styles - A CSSObject or a function returning CSSObject, or a tagged template via `css`.
 * @param selfContainer - Optional. If true, sets `container-type: inline-size;` on the element itself.
 * @returns A styled-components CSS block scoped to the container size range.
 */
export const containerQuery = (
  minWidth: number,
  maxWidth: number,
  styles: CSSObject | ReturnType<typeof css> | (() => CSSObject | ReturnType<typeof css>),
  selfContainer = false
) => css`
  ${selfContainer &&
  css`
    container-type: inline-size;
  `}

  @container (min-width: ${minWidth}px) and (max-width: ${maxWidth}px) {
    ${typeof styles === 'function' ? styles() : styles}
  }
`;

/**
 * Media query utilities for CSS-in-JS.
 */
export const mediaQuery = {
  up,
  down,
  between,
};

export const dvh = (unit = 1) => `calc(var(--cvh) * ${unit})`;

// Opacity map for hex color from hudskit conversion , from 0 to 100;
export const hexWithOpacity = (hex: string, opacity: number): string => {
  const cleanHex = hex.replace('#', '');

  // Ensure opacity is a valid number and within the range of 0 to 1 or 0 to 100
  let normalizedOpacity = isNaN(opacity) ? 0 : opacity;
  if (normalizedOpacity >= 0 && normalizedOpacity <= 1) {
    normalizedOpacity = normalizedOpacity * 100; // Convert decimal to percentage
  }
  normalizedOpacity = Math.min(100, Math.max(0, normalizedOpacity)); // Cap between 0 and 100

  const alpha =
    opacityMap[Math.round(normalizedOpacity).toString() as keyof typeof opacityMap] ?? 'FF';
  return `#${cleanHex}${alpha}`;
};

export type FunctionalColor =
  | 'error'
  | 'caution'
  | 'success'
  | 'progress'
  | 'connectivity'
  | 'other';

/**
 * Gets the RGB color for the given functional color
 * @param theme Current active theme
 * @param color Functional Color to render
 * @returns RGB Color from theme from the specified Functional Color
 */
export const getFunctionalColor = (theme: DefaultTheme, color?: FunctionalColor) => {
  switch (color) {
    case 'error':
      return theme.colors.red500;
    case 'caution':
      return theme.colors.yellow500;
    case 'success':
      return theme.colors.green500;
    case 'progress':
      return theme.colors.green500;
    case 'connectivity':
      return theme.colors.blue500;
    case 'other':
      // Assuming light theme default if darkTheme comparison is not available
      return theme.colors.grey400;
    default:
      return 'inherit';
  }
};

export const statusBadgeColor = (status?: OperationalStatusEnum): FunctionalColor => {
  switch (status) {
    case OperationalStatusEnum.OK:
    case OperationalStatusEnum.OK_WITH_INFO:
      return 'success';
    case OperationalStatusEnum.WARNING:
      return 'caution';
    case OperationalStatusEnum.CRITICAL:
      return 'error';
    case OperationalStatusEnum.NO_CONNECTIVITY_SUPPORT:
      return 'connectivity';
    case OperationalStatusEnum.UNKNOWN:
    default:
      return 'other';
  }
};

export const dragGutterHeight = 60;
